import React from 'react';
import { Box } from '@chakra-ui/react';

/**
 * OptimizedImage - Serves WebP with automatic fallback to original format.
 * Uses <picture> element for native browser format selection.
 *
 * Props:
 *   src - Original image path (e.g. "/facility/exterior.jpg")
 *   alt - Alt text
 *   ...rest - Any other props passed to the <img> tag (style, className, etc.)
 *
 * Automatically generates a .webp srcSet from the original src path.
 * Supports eager/lazy loading via the loading prop (defaults to "lazy").
 */
const OptimizedImage = ({ src, alt, loading = 'lazy', ...rest }) => {
  // Generate WebP path from original
  const webpSrc = src.replace(/\.(jpg|jpeg|png)$/i, '.webp');

  // Determine source MIME type
  const ext = src.split('.').pop().toLowerCase();
  const mimeMap = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' };
  const fallbackType = mimeMap[ext] || 'image/jpeg';

  return (
    <Box as="picture" display="contents">
      <source srcSet={webpSrc} type="image/webp" />
      <source srcSet={src} type={fallbackType} />
      <Box
        as="img"
        src={src}
        alt={alt || ''}
        loading={loading}
        decoding="async"
        {...rest}
      />
    </Box>
  );
};

export default OptimizedImage;
